import sharp from 'sharp';
import fs from 'fs-extra';
import path from 'path';

export interface ImageTemplate {
  fileName: string;
  pixelDensity: number;
  directory?: string;
}

export interface ImageResizeOptions {
  width?: number;
  height?: number;
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  background?: string;
}

export class ImageProcessor {
  static async processImage(
    sourcePath: string,
    templates: ImageTemplate[],
    baseOutputPath: string,
    options: ImageResizeOptions = {}
  ): Promise<void> {
    const sourceImage = sharp(sourcePath);
    const { width: originalWidth, height: originalHeight } = await sourceImage.metadata();

    if (!originalWidth || !originalHeight) {
      throw new Error(`Unable to get dimensions for image: ${sourcePath}`);
    }

    await Promise.all(
      templates.map(async template => {
        const outputDir = template.directory
          ? path.join(baseOutputPath, template.directory)
          : baseOutputPath;

        const outputPath = path.join(outputDir, template.fileName);

        // Ensure output directory exists
        await fs.ensureDir(outputDir);

        // Calculate target dimensions
        const targetWidth = Math.round((originalWidth * template.pixelDensity) / 4);
        const targetHeight = Math.round((originalHeight * template.pixelDensity) / 4);

        // Resize and save image
        await sourceImage
          .resize({
            width: options.width || targetWidth,
            height: options.height || targetHeight,
            fit: options.fit || 'inside',
            background: options.background || { r: 255, g: 255, b: 255, alpha: 0 },
          })
          .png()
          .toFile(outputPath);
      })
    );
  }

  static async createSolidColorImage(
    color: string,
    width: number,
    height: number,
    outputPath: string
  ): Promise<void> {
    await fs.ensureDir(path.dirname(outputPath));

    const { r, g, b } = this.hexToRgb(color);

    await sharp({
      create: {
        width,
        height,
        channels: 3,
        background: { r, g, b },
      },
    })
      .png()
      .toFile(outputPath);
  }

  static async copyAndConvertImage(
    sourcePath: string,
    outputPath: string,
    targetFormat: 'png' | 'jpg' | 'gif' = 'png'
  ): Promise<void> {
    await fs.ensureDir(path.dirname(outputPath));

    const sourceExt = path.extname(sourcePath).toLowerCase();
    const targetExt = path.extname(outputPath).toLowerCase();

    // If same format, just copy
    if (sourceExt === `.${targetFormat}` && sourceExt === targetExt) {
      await fs.copy(sourcePath, outputPath);
      return;
    }

    // Convert using Sharp
    let processor = sharp(sourcePath);

    switch (targetFormat) {
      case 'png':
        processor = processor.png();
        break;
      case 'jpg':
        processor = processor.jpeg();
        break;
      case 'gif':
        // Sharp doesn't support GIF output, copy if source is GIF
        if (sourceExt === '.gif') {
          await fs.copy(sourcePath, outputPath);
          return;
        }
        processor = processor.png(); // Fallback to PNG
        break;
    }

    await processor.toFile(outputPath);
  }

  static hexToRgb(hex: string): { r: number; g: number; b: number } {
    const cleanHex = hex.replace('#', '');
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    return { r, g, b };
  }

  static async getImageDimensions(imagePath: string): Promise<{ width: number; height: number }> {
    const metadata = await sharp(imagePath).metadata();
    if (!metadata.width || !metadata.height) {
      throw new Error(`Unable to get dimensions for image: ${imagePath}`);
    }
    return { width: metadata.width, height: metadata.height };
  }

  // Android density templates
  static getAndroidTemplates(dark = false, android12 = false): ImageTemplate[] {
    const prefix = dark ? 'drawable-night' : 'drawable';
    const suffix = android12 ? '-v31' : '';
    const templates: ImageTemplate[] = [];

    // Regular splash screen templates
    const densities = [
      { name: 'mdpi', scale: 1 },
      { name: 'hdpi', scale: 1.5 },
      { name: 'xhdpi', scale: 2 },
      { name: 'xxhdpi', scale: 3 },
      { name: 'xxxhdpi', scale: 4 },
    ];

    // Add regular splash.png for each density
    densities.forEach(density => {
      templates.push({
        fileName: 'splash.png',
        pixelDensity: density.scale,
        directory: `${prefix}-${density.name}${suffix}`,
      });

      // If Android 12 support is enabled, add android12splash.png to drawable folder
      if (android12) {
        // Add to regular drawable
        templates.push({
          fileName: 'android12splash.png',
          pixelDensity: density.scale, // Use highest quality for Android 12
          directory: `${prefix}-${density.name}${suffix}`,
        });

        // Add to night drawable if dark mode
        if (dark) {
          templates.push({
            fileName: 'android12splash.png',
            pixelDensity: density.scale, // Use highest quality for Android 12
            directory: 'drawable-night',
          });
        }

        // Optional: Add branding image if needed
        templates.push({
          fileName: 'android12branding.png',
          pixelDensity: 4,
          directory: 'drawable',
        });
      }
    });

    return templates;
  }

  // iOS density templates
  static getIOSTemplates(imageName = 'LaunchImage', dark = false): ImageTemplate[] {
    const suffix = dark ? 'Dark' : '';
    return [
      { fileName: `${imageName}${suffix}.png`, pixelDensity: 1 },
      { fileName: `${imageName}${suffix}@2x.png`, pixelDensity: 2 },
      { fileName: `${imageName}${suffix}@3x.png`, pixelDensity: 3 },
    ];
  }

  // Web density templates
  static getWebTemplates(prefix = 'light', extension = 'png'): ImageTemplate[] {
    return [
      { fileName: `${prefix}-1x.${extension}`, pixelDensity: 1 },
      { fileName: `${prefix}-2x.${extension}`, pixelDensity: 2 },
      { fileName: `${prefix}-3x.${extension}`, pixelDensity: 3 },
      { fileName: `${prefix}-4x.${extension}`, pixelDensity: 4 },
    ];
  }
}
